import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, describeError } from "../../lib/api";
import { Avatar, Spinner } from "../../components/ui";
import { Cover, authorsOf } from "../../components/BookBits";
import { Stars } from "../../components/Stars";
import { CheckIcon, LockIcon, MoreIcon, NoteIcon, TalkIcon, ChevronIcon } from "../../components/Icons";
import { useClub } from "./ClubLayout";
import { pickedLine } from "./ShelfTab";
import { MineBlock } from "../../components/MineBlock";
import { BackBar } from "../../components/ui";
import { DatesEditor } from "../../components/DatesEditor";
import { Envelope, PaperNote, PredictionList, spot } from "../../components/Moments";
import { PREDICTION_WINDOW } from "@pagemates/shared";
import type { PosCtx } from "../../lib/format";
import { useMe } from "../../lib/queries";
import { shelf } from "@pagemates/shared/copy";

const txt = shelf.recap;

const d = (iso: string | null, opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" }) => (iso ? new Date(iso).toLocaleDateString(undefined, opts) : "");

/*
 * A finished book's page (PLAN.md §2 Shelf, reshaped 2026-11-03): the club's
 * record of it, in the Reading tab's shape. The poster, you, the club's
 * verdict, first reactions, the meetings, lines we kept, the conversation,
 * and the blurb folded at the end. Tapping a finished book on the shelf
 * lands here; the pane is for on-deck books only.
 */
export function RecapPage() {
  const { clubId } = useClub();
  const { bookId } = useParams({ from: "/authed/clubs/$clubId/recap/$bookId" });
  return <RecapView clubId={clubId} bookId={bookId} />;
}

/** The recap itself: the page on desktop (and for direct links), the sheet over the shelf on a phone. */
export function RecapView({ clubId, bookId, inSheet = false }: { clubId: string; bookId: string; inSheet?: boolean }) {
  const { club } = useClub();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["recap", clubId, bookId], queryFn: () => api.recap(clubId, bookId) });
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ["recap", clubId, bookId] }), qc.invalidateQueries({ queryKey: ["books", clubId] })]);
  const [openRow, setOpenRow] = useState<string | null>(null);
  const me = useMe();
  const meId = me.data?.id;
  const Wrap = inSheet ? "div" : "main";
  if (q.isPending) return <Wrap className={inSheet ? "recap-body" : "page"}><Spinner /></Wrap>;
  if (q.isError) return <Wrap className={inSheet ? "recap-body" : "page"}><p className="error">{describeError(q.error)}</p></Wrap>;
  const r = q.data;
  const b = r.book;
  const isAdmin = club.data?.myRole !== "member";
  const finishedReaders = r.readers.filter((x) => x.status === "finished");
  const maxCount = Math.max(1, ...r.ratings.distribution.map((x) => x.count));
  const shownDist = r.ratings.distribution.filter((x) => Number.isInteger(x.stars) || x.count > 0);
  const starsOf = new Map(r.ratings.members.map((m) => [m.id, m.stars]));
  // Everyone who didn't rate, by what they did; each row opens to its names, like the rating bars above.
  const unrated = r.readers.filter((m) => !starsOf.has(m.id));
  const restRows: { key: string; label: string; people: typeof r.readers; pct?: boolean }[] = [
    { key: "fin", label: txt.finishedNoRating, people: unrated.filter((m) => m.status === "finished") },
    { key: "dnf", label: txt.stoppedPartway, people: unrated.filter((m) => m.status === "dnf").sort((a, b) => b.percent - a.percent), pct: true },
    { key: "reading", label: txt.stillReading, people: unrated.filter((m) => m.status === "reading" && m.percent > 0).sort((a, b) => b.percent - a.percent), pct: true },
    { key: "before", label: txt.readItBefore, people: unrated.filter((m) => m.status === "read_before") },
    { key: "none", label: txt.neverStarted, people: unrated.filter((m) => m.status === "reading" && m.percent === 0) },
  ];
  const tags = [...(b.book.genres ?? []), ...(b.book.moods ?? [])];
  const ctx: PosCtx = { pages: b.book.pages, audioSeconds: b.book.audioSeconds, myFormat: b.myFormat, landmarks: b.plan?.landmarks ?? [] };
  // Notes for everyone are the club's record; a note for one person stays between its writer and its reader.
  const everyoneNotes = r.forwardNotes.filter((n) => !n.recipient);
  const privateNotes = r.forwardNotes.filter((n) => n.recipient);
  const meetings = r.meetings.length;

  return (
    <Wrap className={inSheet ? "now recap recap-body" : "page now recap"}>
      <div className="now-main stack">
        {!inSheet && <BackBar label={txt.shelf} link={{ to: "/clubs/$clubId/shelf", params: { clubId } }} />}

        <section className="hero">
          {b.book.coverUrl && <div className="art" aria-hidden="true"><img src={b.book.coverUrl} alt="" /></div>}
          <Cover book={b.book} size="lg" />
          <div className="grow" style={{ minWidth: 0 }}>
            <div className="t">{b.book.title}</div>
            <div className="a">{authorsOf(b.book)}{b.book.releaseYear ? ` · ${b.book.releaseYear}` : ""}{b.book.pages ? txt.pages(b.book.pages) : ""}</div>
            <div className="meta">
              <span>{b.startedAt ? `${d(b.startedAt, { month: "short", day: "numeric" })} – ` : txt.finished}{d(b.finishedAt)}{r.days ? <> · <b className="n">{r.days}</b>{txt.days}</> : null}{meetings > 0 ? <> · <b className="n">{meetings}</b> {meetings === 1 ? txt.meeting : txt.meetings}</> : null}</span>
              <span className="muted">{txt.ofFinished(finishedReaders.length, r.readers.length, pickedLine(b.picked) ? ` · ${pickedLine(b.picked)!.charAt(0).toLowerCase()}${pickedLine(b.picked)!.slice(1)}` : "")}</span>
            </div>
          </div>
          {isAdmin && <RecapAdmin clubId={clubId} book={b} onChange={refresh} />}
        </section>

        <MineBlock clubId={clubId} book={b} onChange={refresh} />

        {r.readers.some((x) => x.reaction) && (
          <section>
            <div className="sect"><h3>{txt.firstReactions}</h3></div>
            <div className="stack-sm" style={{ marginTop: 8 }}>
              {r.readers.filter((x) => x.reaction).map((x) => <div key={x.id} className="row" style={{ alignItems: "flex-start" }}><Avatar name={x.name} image={x.image} /><p className="book" style={{ margin: 0 }}>“{x.reaction}”<span className="muted small"> · {x.name}</span></p></div>)}
            </div>
          </section>
        )}
        {!r.readers.some((x) => x.reaction) && r.readers.some((x) => x.status !== "reading") && r.discussion.sealedForYou > 0 && (
          <p className="muted small row" style={{ gap: 6 }}><LockIcon width={14} height={14} />{txt.firstReactionsUnlockWhen}</p>
        )}

        {r.meetings.length > 0 && (
          <section>
            <div className="sect"><h3>{meetings === 1 ? txt.theMeeting : txt.theMeetings}</h3><span className="count">{r.meetings.map((m) => d(m.at, { month: "short", day: "numeric" })).join(" · ")}</span></div>
            <div className="stack-sm" style={{ marginTop: 8 }}>
              {r.meetings.map((m) => (
                <div key={m.id} className="fill stack-sm">
                  <div className="row spread small muted"><span>{d(m.at, { weekday: "long", month: "short", day: "numeric" })}</span><span>{m.host ? txt.hosted(m.host.name) : ""}</span></div>
                  {m.sealed ? <p className="muted small row" style={{ gap: 6, margin: 0 }}><LockIcon width={14} height={14} />{txt.sealedUntilYouFinish}</p> : (
                    <>
                      {m.notes ? <p className="book notes-text">{m.notes}</p> : <p className="muted small" style={{ margin: 0 }}>{txt.nobodyWroteANote}</p>}
                      {m.questions.length > 0 && (
                        <div className="stack-sm" style={{ paddingTop: 4 }}>
                          <div className="small muted">{txt.whatWeTalkedAbout}</div>
                          {m.questions.map((qq) => <div key={qq.id} className="row small" style={{ alignItems: "flex-start", gap: 8 }}><CheckIcon width={16} height={16} style={{ color: "var(--moss-text)", flex: "none" }} /><span><span className="book">{qq.body}</span> <span className="muted">{txt.sQuestion(qq.author.name)}</span></span></div>)}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {r.agendaDone.length > 0 && (
          <section>
            <div className="sect"><h3>{txt.talkedAbout}</h3><span className="count">{r.agendaDone.length}</span></div>
            <div className="stack-sm" style={{ marginTop: 8 }}>
              {r.agendaDone.map((i) => <div key={i.key} className="row small" style={{ alignItems: "flex-start", gap: 8 }}><CheckIcon width={16} height={16} style={{ color: "var(--moss-text)", flex: "none" }} /><span className={i.kind === "thread" || i.kind === "most-replied" ? "book" : ""}>{i.title}</span></div>)}
            </div>
          </section>
        )}

        {r.quotes.length > 0 && (
          <section>
            <div className="sect"><h3>{txt.linesWeKept}</h3><span className="count">{r.quotes.length}</span></div>
            <div className="stack-sm" style={{ marginTop: 8 }}>
              {r.quotes.map((qq) => (
                <Link key={qq.id} to="/clubs/$clubId/talk" params={{ clubId }} search={{ book: b.id, thread: qq.id }} className="quote-item fill">
                  {qq.body ? <blockquote className="qblock"><span className="qmark" aria-hidden="true">“</span>{qq.body}</blockquote>
                    : <p className="muted small row" style={{ gap: 6, margin: 0 }}><LockIcon width={14} height={14} />{txt.sealedUntil(qq.position)}</p>}
                  <div className="small muted">{qq.author.name} · {qq.position}%{qq.replyCount ? ` · ${qq.replyCount} ${qq.replyCount === 1 ? txt.reply : txt.replies}` : ""}</div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {r.predictions.length > 0 && (
          <section>
            <div className="sect"><h3>{txt.predictions}</h3><span className="count">{r.predictions.length}</span></div>
            {r.predictions.some((x) => x.body !== null && !x.mine) || r.predictions.every((x) => x.mine) ? (
              <>
                <p className="small muted" style={{ margin: "2px 0 10px" }}>{txt.sealedBeforeOpenedAt(spot(PREDICTION_WINDOW, ctx))}</p>
                <PredictionList all={r.predictions} ctx={ctx} grid />
              </>
            ) : <p className="muted small row" style={{ gap: 6, marginTop: 8 }}><LockIcon width={14} height={14} />{txt.theyOpenWhenYou}</p>}
          </section>
        )}

        {everyoneNotes.length > 0 && (
          <section>
            <div className="sect"><h3>{txt.notesLeftInThis}</h3><span className="count">{everyoneNotes.length}</span></div>
            <p className="small muted" style={{ margin: "2px 0 8px" }}>{txt.everyNoteSomeoneLeft}</p>
            <div className="list fwd-list">
              {everyoneNotes.map((n) => (
                <div key={n.id} className="row" style={{ alignItems: "flex-start" }}>
                  <Avatar name={n.author.name} image={n.author.image} />
                  <div style={{ minWidth: 0 }}><div className="small muted">{txt.at(n.mine ? txt.you : n.author.name, spot(n.position, ctx), n.editedAt ? txt.edited : "")}</div><p className="book" style={{ margin: 0 }}>{n.body}</p></div>
                </div>
              ))}
            </div>
          </section>
        )}

        {(b.book.description || tags.length > 0 || (b.book.contentWarnings?.length ?? 0) > 0 || b.book.url) && (
          <details className="fold about">
            <summary><span className="sect"><h3>{txt.aboutTheBook}</h3></span><ChevronIcon className="chev" width={16} height={16} aria-hidden="true" /></summary>
            <div className="stack-sm" style={{ marginTop: 10 }}>
              {b.book.description && <p className="desc" style={{ margin: 0 }}>{b.book.description.replace(/\*\*?|__?/g, "")}</p>}
              {tags.length > 0 && <div className="pills">{tags.map((t) => <span key={t} className="pill">{t}</span>)}</div>}
              {(b.book.contentWarnings?.length ?? 0) > 0 && <div className="pills"><span className="small muted">{txt.contentWarnings}</span>{b.book.contentWarnings!.map((t) => <span key={t} className="pill">{t}</span>)}</div>}
              {b.book.url && <a href={b.book.url} target="_blank" rel="noopener" className="hc-link">{txt.openOnHardcover}</a>}
            </div>
          </details>
        )}
      </div>

      <div className="now-aside stack-sm">
        <div className="sect"><h3>{txt.theClubSVerdict}</h3>{r.ratings.average !== null && <span className="count">{r.ratings.count} {r.ratings.count === 1 ? txt.rating : txt.ratings}</span>}</div>
        <div className="fill stack-sm">
          {r.ratings.average === null ? <p className="muted small" style={{ margin: 0 }}>{txt.nobodyHasRatedIt}</p> : (
            <>
              <div className="row" style={{ gap: 12, alignItems: "baseline" }}>
                <span className="big">{r.ratings.average.toFixed(1)}</span>
                <Stars value={r.ratings.average} />
                {r.ratings.dnfCount > 0 && <span className="muted small">{txt.didnTFinish(r.ratings.dnfCount)}</span>}
              </div>
              <div className="dist">
                {shownDist.map((x) => {
                  const key = `s${x.stars}`;
                  const who = r.readers.filter((m) => starsOf.get(m.id) === x.stars);
                  return (
                    <VerdictRow key={key} id={key} open={openRow === key} onToggle={() => setOpenRow((o) => (o === key ? null : key))}
                      label={<span className="num small muted">{x.stars}</span>} count={x.count} bar={x.count / maxCount} people={who} meId={meId} />
                  );
                })}
              </div>
            </>
          )}
          {/* Everyone who didn't rate, as rows without a bar: the same tap opens their names. */}
          <div className="dist rest">
            {restRows.map((g) => g.people.length > 0 && (
              <VerdictRow key={g.key} id={g.key} open={openRow === g.key} onToggle={() => setOpenRow((o) => (o === g.key ? null : g.key))}
                label={<span className="small muted">{g.label}</span>} count={g.people.length} people={g.people} meId={meId} pct={g.pct} />
            ))}
          </div>
        </div>

        <div className="sect"><h3>{txt.theConversation}</h3>{r.discussion.messages > 0 && <span className="count">{r.discussion.messages} {r.discussion.messages === 1 ? txt.message : txt.messages}</span>}</div>
        {r.discussion.messages === 0 ? (
          <Link to="/clubs/$clubId/talk" params={{ clubId }} search={{ book: b.id }} className="quiet link"><TalkIcon /><div><b>{txt.nobodySaidAnything}</b><div className="d">{txt.openTalkAndBe}</div></div><ChevronIcon className="go" /></Link>
        ) : (
          <>
            {r.discussion.topThread && (
              <Link to="/clubs/$clubId/talk" params={{ clubId }} search={{ book: b.id, thread: r.discussion.topThread.id }} className="fill quote stack-sm">
                <div className="row small muted" style={{ gap: 8 }}><Avatar name={r.discussion.topThread.author.name} image={r.discussion.topThread.author.image} /><span>{txt.mostReplied(r.discussion.topThread.author.name, r.discussion.topThread.replyCount, r.discussion.topThread.replyCount === 1 ? txt.reply : txt.replies)}</span></div>
                {r.discussion.topThread.body ? <p className="book" style={{ margin: 0 }}>“{r.discussion.topThread.body}”</p>
                  : <p className="muted small row" style={{ gap: 6, margin: 0 }}><LockIcon width={14} height={14} />{txt.sealedUntilReadOn(r.discussion.topThread.position)}</p>}
              </Link>
            )}
            <Link to="/clubs/$clubId/talk" params={{ clubId }} search={{ book: b.id }} className="quiet link">
              <TalkIcon />
              <div style={{ minWidth: 0 }}><b className="n">{r.discussion.threads} {r.discussion.threads === 1 ? txt.thread : txt.threads}</b>
                <div className="d">{r.discussion.participants.map((p) => p.name).join(", ")}{r.discussion.sealedForYou > 0 ? txt.sealedForYou(r.discussion.sealedForYou) : ""}{r.discussion.firstMessageAt ? txt.to(d(r.discussion.firstMessageAt, { month: "short", day: "numeric" }), d(r.discussion.lastMessageAt, { month: "short", day: "numeric" })) : ""}</div></div>
              <ChevronIcon className="go" />
            </Link>
          </>
        )}

        <Link to="/clubs/$clubId/notes/$bookId" params={{ clubId, bookId }} className="quiet link"><NoteIcon /><div><b>{r.myNotes > 0 ? txt.yourNotes(r.myNotes) : txt.yourNotes2}</b><div className="d">{txt.onlyYouCanSee}</div></div><ChevronIcon className="go" /></Link>
        {privateNotes.length > 0 && (
          <div className="stack-sm private-notes">
            {privateNotes.map((n) => n.mine ? (
              <div key={n.id} className="quiet"><Envelope /><div style={{ minWidth: 0 }}><b className="book">{n.body}</b><div className="d">{txt.youLeftThisAt(spot(n.position, ctx), n.recipient!.name)}</div></div></div>
            ) : <PaperNote key={n.id} n={n} ctx={ctx} clubId={clubId} bookId={bookId} />)}
          </div>
        )}
      </div>
    </Wrap>
  );
}

/** The admin's ⋯ on the poster: edit the dates, or reopen a book finished by mistake. */
function RecapAdmin({ clubId, book, onChange }: { clubId: string; book: import("@pagemates/shared").ClubBook; onChange: () => Promise<unknown> }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [reopening, setReopening] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const reopen = useMutation({ mutationFn: () => api.setBookStatus(clubId, book.id, "current"), onSuccess: onChange });
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  return (
    <div className="hero-menu" ref={ref}>
      <button type="button" className="more" aria-label={txt.bookActions} aria-expanded={open} onClick={() => setOpen((o) => !o)}><MoreIcon /></button>
      {open && (
        <div className="menu" role="menu">
          <button role="menuitem" onClick={() => { setOpen(false); setEditing(true); }}>{txt.editDates}</button>
          <button role="menuitem" onClick={() => { setOpen(false); setReopening(true); }}>{txt.reopenMakeItCurrent}</button>
        </div>
      )}
      {editing && <div className="hero-confirm" style={{ padding: 0, border: 0 }}><DatesEditor clubId={clubId} book={book} onDone={() => { setEditing(false); void onChange(); }} /></div>}
      {reopening && (
        <div className="confirm hero-confirm">
          <p className="small">{txt.finishedByMistakeMake}<b>{book.book.title}</b>{txt.currentAgainRatingsReactions}</p>
          <div className="row"><button className="btn primary sm" disabled={reopen.isPending} onClick={() => reopen.mutate()}>{txt.reopen}</button><button className="btn ghost sm" onClick={() => setReopening(false)}>{txt.cancel}</button></div>
          {reopen.isError && <p className="error">{describeError(reopen.error)}</p>}
        </div>
      )}
    </div>
  );
}

/**
 * One line of the club's verdict: a rating with its bar, or a group of
 * members who didn't rate. Tapping it opens the names beneath, you first.
 */
function VerdictRow({ id, label, count, bar, people, meId, pct, open, onToggle }:
  { id: string; label: React.ReactNode; count: number; bar?: number; people: { id: string; name: string; image: string | null; percent: number; former?: boolean }[]; meId?: string; pct?: boolean; open: boolean; onToggle: () => void }) {
  const [all, setAll] = useState(false);
  const [q, setQ] = useState("");
  useEffect(() => { if (!open) { setAll(false); setQ(""); } }, [open]);
  const t = q.trim().toLowerCase();
  const list = [...people].sort((a, b) => (a.id === meId ? -1 : b.id === meId ? 1 : 0)).filter((m) => !t || m.name.toLowerCase().includes(t));
  const shown = all ? list : list.slice(0, VERDICT_NAMES);
  return (
    <>
      <button type="button" className={`dist-row vrow${bar === undefined ? " nobar" : ""}${open ? " open" : ""}`} aria-expanded={open} aria-controls={`v-${id}`} disabled={count === 0} onClick={onToggle}>
        {label}
        {bar !== undefined && <span className="bar"><i style={{ width: `${bar * 100}%` }} /></span>}
        <span className="num small muted">{count || ""}</span>
      </button>
      {open && (
        <div id={`v-${id}`} className={`vbox${all ? " all" : ""}`}>
          {all && people.length > 50 && <input id={`v-find-${id}`} className="input sm vfind" type="search" placeholder={txt.findSomeone} value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" autoFocus />}
          <div className="vnames">
            {shown.map((m) => (
              <span key={m.id} className="vname"><Avatar name={m.name} image={m.image} />{m.id === meId ? txt.you : m.name}{pct && <span className="muted"> · {m.percent}%</span>}{m.former && <span className="muted">{txt.left}</span>}</span>
            ))}
            {t && list.length === 0 && <span className="muted small">{txt.nobodyByThatName}</span>}
          </div>
          {!all && list.length > VERDICT_NAMES && <button type="button" className="linkish small vmore" onClick={() => setAll(true)}>{txt.showAll(list.length)}</button>}
        </div>
      )}
    </>
  );
}
/** Names shown when a verdict row opens, before "Show all". */
const VERDICT_NAMES = 24;
